import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { KeyRound, Plus, Loader2, DollarSign, TrendingDown, Trash2, Calculator } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── License Utilization ──

export function LicenseTracker({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', platform_type_id: '', licenses_purchased: 0, licenses_active: 0, cost_per_license: 0, license_type: 'per_user' });
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, props, plats] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/license-utilization`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setData(r.data);
      setProperties(props.data || []);
      setPlatforms(plats.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/licenses`, form, { headers });
      toast.success('License data saved');
      setAddOpen(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="license-tracker">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>License & Seat Utilization</h2>
          <p className="text-sm text-muted-foreground">Track purchased vs. active licenses to identify waste and savings.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="gap-1 text-xs" data-testid="add-license-btn"><Plus className="h-3 w-3" /> Add License Data</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.overall_utilization || 0}%</div>
          <div className="text-[10px] text-muted-foreground">Utilization</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.total_licenses || 0}</div>
          <div className="text-[10px] text-muted-foreground">Purchased</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{s.active_licenses || 0}</div>
          <div className="text-[10px] text-muted-foreground">Active</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-red-500" style={{ fontFamily: 'Space Grotesk' }}>{s.unused_licenses || 0}</div>
          <div className="text-[10px] text-muted-foreground">Unused</div>
        </div>
        <div className="p-3 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk' }}>${(s.annual_waste || 0).toLocaleString()}</div>
          <div className="text-[10px] text-muted-foreground">Annual Waste</div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50">
            <th className="p-2.5 text-left text-xs font-semibold">Property</th>
            <th className="p-2.5 text-left text-xs font-semibold">Platform</th>
            <th className="p-2.5 text-center text-xs font-semibold">Purchased</th>
            <th className="p-2.5 text-center text-xs font-semibold">Active</th>
            <th className="p-2.5 text-center text-xs font-semibold">Unused</th>
            <th className="p-2.5 text-center text-xs font-semibold">$/License</th>
            <th className="p-2.5 text-center text-xs font-semibold text-red-600">Monthly Waste</th>
            <th className="p-2.5 text-center text-xs font-semibold">Utilization</th>
          </tr></thead>
          <tbody>
            {(data?.licenses || []).map((l, i) => (
              <tr key={i} className="border-t hover:bg-muted/20">
                <td className="p-2.5 font-medium">{l.property_name}</td>
                <td className="p-2.5">{l.platform}</td>
                <td className="p-2.5 text-center font-mono text-xs">{l.purchased}</td>
                <td className="p-2.5 text-center font-mono text-xs text-emerald-600">{l.active}</td>
                <td className="p-2.5 text-center font-mono text-xs text-red-500">{l.unused}</td>
                <td className="p-2.5 text-center font-mono text-xs">${l.cost_per_license}</td>
                <td className="p-2.5 text-center font-mono text-xs text-red-600">${l.monthly_waste}</td>
                <td className="p-2.5 text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${l.utilization_pct}%`, backgroundColor: l.utilization_pct >= 80 ? '#16a34a' : l.utilization_pct >= 50 ? '#d97706' : '#dc2626' }} /></div>
                    <span className="text-[10px] font-mono">{l.utilization_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
            {(data?.licenses || []).length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">No license data tracked yet. Add your first record above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add License Data</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Property</label>
              <Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium">Platform</label>
              <Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs font-medium">Purchased</label><Input type="number" value={form.licenses_purchased} onChange={e => setForm(p => ({ ...p, licenses_purchased: parseInt(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" /></div>
              <div><label className="text-xs font-medium">Active</label><Input type="number" value={form.licenses_active} onChange={e => setForm(p => ({ ...p, licenses_active: parseInt(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" /></div>
              <div><label className="text-xs font-medium">$/License</label><Input type="number" step="0.01" value={form.cost_per_license} onChange={e => setForm(p => ({ ...p, cost_per_license: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" /></div>
            </div>
            <div><label className="text-sm font-medium">License Type</label>
              <Select value={form.license_type} onValueChange={v => setForm(p => ({ ...p, license_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_user">Per User</SelectItem>
                  <SelectItem value="per_device">Per Device</SelectItem>
                  <SelectItem value="per_room">Per Room</SelectItem>
                  <SelectItem value="site">Site License</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.property_id || !form.platform_type_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── Budget Planning ──

export function BudgetPlanner({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scenarios, setScenarios] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', changes: [{ action: 'add', description: '', estimated_cost: 0 }] });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/budget-overview`, { headers }),
        axios.get(`${BACKEND_URL}/api/budget-scenarios`, { headers }),
      ]);
      setData(r.data);
      setScenarios(s.data.scenarios || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addChange = () => setForm(p => ({ ...p, changes: [...p.changes, { action: 'add', description: '', estimated_cost: 0 }] }));
  const removeChange = (i) => setForm(p => ({ ...p, changes: p.changes.filter((_, j) => j !== i) }));
  const updateChange = (i, field, val) => setForm(p => ({ ...p, changes: p.changes.map((c, j) => j === i ? { ...c, [field]: val } : c) }));

  const totalDelta = form.changes.reduce((s, c) => s + (c.action === 'remove' ? -(c.estimated_cost || 0) : (c.estimated_cost || 0)), 0);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/budget-scenarios`, form, { headers });
      toast.success('Scenario created');
      setCreateOpen(false);
      setForm({ name: '', description: '', changes: [{ action: 'add', description: '', estimated_cost: 0 }] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteScenario = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/budget-scenarios/${id}`, { headers });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="budget-planner">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Technology Budget Planning</h2>
          <p className="text-sm text-muted-foreground">Model scenarios: add platforms, consolidate vendors, forecast spend.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="gap-1 text-xs" data-testid="create-scenario-btn"><Plus className="h-3 w-3" /> New Scenario</Button>
      </div>

      {/* Current spend overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(data?.current_annual_spend || 0).toLocaleString()}</div>
          <div className="text-[10px] text-muted-foreground">Current Annual Spend</div>
        </div>
        <div className="p-4 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.total_contracts || 0}</div>
          <div className="text-[10px] text-muted-foreground">Tracked Contracts</div>
        </div>
        <div className="p-4 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(data?.avg_per_property || 0).toLocaleString()}</div>
          <div className="text-[10px] text-muted-foreground">Avg / Property</div>
        </div>
        <div className="p-4 rounded-lg border bg-card text-center">
          <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{scenarios.length}</div>
          <div className="text-[10px] text-muted-foreground">Saved Scenarios</div>
        </div>
      </div>

      {/* Saved scenarios */}
      {scenarios.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Saved Scenarios</h3>
          {scenarios.map(sc => (
            <div key={sc.id} className="p-4 border rounded-lg bg-card flex items-center justify-between">
              <div>
                <span className="font-medium text-sm">{sc.name}</span>
                {sc.description && <p className="text-xs text-muted-foreground">{sc.description}</p>}
                <div className="flex gap-3 mt-1">
                  <span className="text-xs">Current: <strong>${(sc.current_annual_spend || 0).toLocaleString()}</strong></span>
                  <span className={`text-xs font-bold ${sc.projected_delta >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {sc.projected_delta >= 0 ? '+' : ''}${(sc.projected_delta || 0).toLocaleString()}
                  </span>
                  <span className="text-xs">Projected: <strong>${(sc.projected_total || 0).toLocaleString()}</strong></span>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteScenario(sc.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Spend by property */}
      {(data?.by_property || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Spend by Property</h3>
          <div className="space-y-1">
            {(data?.by_property || []).slice(0, 10).map((p, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="w-48 truncate">{p.name}</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${(p.spend / (data?.current_annual_spend || 1)) * 100}%` }} />
                </div>
                <span className="font-mono text-xs w-20 text-right">${p.spend.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create scenario dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>New Budget Scenario</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 flex-1 overflow-auto">
            <div><label className="text-sm font-medium">Scenario Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Consolidate POS vendors" className="mt-1" data-testid="scenario-name" /></div>
            <div><label className="text-sm font-medium">Description</label><Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Migrate from 3 POS vendors to 1" className="mt-1" /></div>
            <Separator />
            <div className="flex items-center justify-between"><label className="text-sm font-medium">Changes</label><Button variant="ghost" size="sm" onClick={addChange} className="text-xs gap-1"><Plus className="h-3 w-3" /> Add</Button></div>
            {form.changes.map((c, i) => (
              <div key={i} className="flex items-center gap-2 p-2 border rounded-lg">
                <Select value={c.action} onValueChange={v => updateChange(i, 'action', v)}>
                  <SelectTrigger className="w-24 h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add</SelectItem>
                    <SelectItem value="remove">Remove</SelectItem>
                    <SelectItem value="consolidate">Consolidate</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={c.description} onChange={e => updateChange(i, 'description', e.target.value)} placeholder="Description" className="h-7 text-xs flex-1" />
                <Input type="number" value={c.estimated_cost} onChange={e => updateChange(i, 'estimated_cost', parseFloat(e.target.value) || 0)} className="h-7 text-xs w-24" placeholder="$/yr" />
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeChange(i)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
              </div>
            ))}
            <div className="p-3 rounded-lg bg-muted/40 text-sm">
              <div className="flex justify-between">
                <span>Current Annual Spend</span>
                <span className="font-mono">${(data?.current_annual_spend || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Scenario Delta</span>
                <span className={`font-mono font-bold ${totalDelta >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>{totalDelta >= 0 ? '+' : ''}${totalDelta.toLocaleString()}</span>
              </div>
              <Separator className="my-1" />
              <div className="flex justify-between font-bold">
                <span>Projected Total</span>
                <span className="font-mono">${((data?.current_annual_spend || 0) + totalDelta).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save Scenario</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
